/**
 * Geometry of the course metro map: every lesson group is one line, drawn as a row that runs
 * left to right, then right to left, with a rounded turn between rows. Inside a row a lesson
 * sits one column after its prerequisites from the same group, so lessons that can be taken in
 * any order run as parallel tracks, each on its own lane. A fork bends right after the stop it
 * leaves, a join merges into one point just before its stop, and lanes are swapped until no line
 * crosses or runs over another. Bonus lessons hang off the line as dotted spurs.
 */

export const METRO_WIDTH = 936;

const ROW_GAP = 110;
const SPUR_GAP = 96;
const PLAQUE_BAND = 64;
const FIRST_COLUMN = 190;
const WIDE_FIRST_COLUMN = 172;
const HUB = 110;
const SIDE = 50;
const MAX_STACK = 3;
const WIDE_STACK = 4;
const MAX_COLUMNS = 5;
const MAX_COLUMN_GAP = 200;
const CROWDED_GAP = 170;
const LABEL_LIFT = 56;
const LABEL_WIDTH = 150;
const MIN_LABEL_WIDTH = 96;
const STOP_RADIUS = 21;
const TURN_RADIUS = 14;
const EXIT_TURN_RADIUS = 10;
const LANE_STEPS = [ 0, -1, 1, -2, 2, -3, 3, -4, 4, -5, 5 ];
const UNTANGLE_STEPS = 40;
/** The cost of one line crossing or covering another; any of them outweighs every bend. */
const CLASH = 1000;

/** Ends of a row's line that are not lessons: where it enters the row and where it leaves it. */
const IN = '@in';
const OUT = '@out';

export type MetroUnit = {
    id: string;
    title: string;
};

export type MetroLesson = {
    id: string;
    unitId: string;
    previous: string[];
    bonus: boolean;
};

export type Point = {
    x: number;
    y: number;
};

export type MetroStopPlace = Point & {
    unitIndex: number;
    labelAbove: boolean;
    /** Crowded rows narrow their labels so neighbouring titles do not run into each other. */
    labelWidth: number;
};

/** A piece of line; it counts as travelled once every lesson in `lessons` is. */
export type MetroSegment = {
    d: string;
    unitIndex: number;
    lessons: string[];
};

export type MetroRow = {
    unitIndex: number;
    top: number;
    leftToRight: boolean;
};

export type MetroLayout = {
    width: number;
    height: number;
    stops: Record<string, MetroStopPlace>;
    segments: MetroSegment[];
    spurs: string[];
    rows: MetroRow[];
    finish: Point;
};

type Rect = Point & { w: number; h: number };

type PlacedRow = {
    hubOut: Point;
    main: string[];
    bottom: number;
};

type RowInput = {
    unitIndex: number;
    top: number;
    lessons: MetroLesson[];
    byId: Map<string, MetroLesson>;
    isLast: boolean;
};

export function layoutMetro(units: MetroUnit[], lessons: MetroLesson[]): MetroLayout {
    const byId = new Map(lessons.map((lesson) => [ lesson.id, lesson ]));
    const layout: MetroLayout = { width: METRO_WIDTH, height: 0, stops: {}, segments: [], spurs: [], rows: [], finish: { x: 0, y: 0 } };
    const filled = units.filter((unit) => lessons.some((lesson) => lesson.unitId === unit.id));

    let previous: PlacedRow | undefined;
    filled.forEach((unit, unitIndex) => {
        const top = previous?.bottom ?? 24;
        const input = { unitIndex, top, byId, isLast: unitIndex === filled.length - 1, lessons: lessons.filter((lesson) => lesson.unitId === unit.id) };
        const row = placeRow(input, layout, previous);

        previous = row;
    });

    layout.finish = previous?.hubOut ?? { x: HUB, y: 24 };
    layout.height = Math.round((previous?.bottom ?? 24) + 40);

    return layout;
}

/** Where the "you are here" marker of `size` fits near `point` without covering stops, labels or lines. */
export function freeSpotNear(layout: MetroLayout, point: Point, size: { w: number; h: number }): Point {
    const blocked = obstacles(layout);
    const lines = sampleLines([ ...layout.segments.map((segment) => segment.d), ...layout.spurs ]);
    const candidates: Rect[] = [];

    for (let dy = -160; dy <= 120; dy += 10) {
        for (let dx = -300; dx <= 200; dx += 10) candidates.push({ x: point.x + dx, y: point.y + dy, ...size });
    }

    // Nearest first, so the search stops at the first free spot instead of testing every candidate
    const distance = (rect: Rect) => Math.hypot(rect.x + 20 - point.x, rect.y + size.h / 2 - point.y);
    const free = candidates
        .sort((a, b) => distance(a) - distance(b))
        .find((rect) => fits(rect, layout) && !blocked.some((box) => overlaps(rect, box)) && !lines.some((p) => near(rect, p)));

    return free ?? { x: point.x + 34, y: point.y - 74 };
}

function placeRow(input: RowInput, layout: MetroLayout, previous?: PlacedRow): PlacedRow {
    const { unitIndex, top, lessons, isLast } = input;
    const leftToRight = unitIndex % 2 === 0;
    const main = lessons.filter((lesson) => !lesson.bonus);
    const depth = depthOf(input);
    const grid = buildGrid(input, depth);
    const geometry = rowGeometry(grid.columns.length, leftToRight, isLast);
    const lanes = assignLanes(input, grid);
    const spurs = arrangeSpurs(input, grid, lanes, depth);

    const used = [ 0, ...lanes.values() ];
    const upRows = rowCount(spurs, false), downRows = rowCount(spurs, true);
    const mid = top + PLAQUE_BAND + upRows * SPUR_GAP + (upRows ? geometry.lift : 0) + 30 + geometry.lift - Math.min(...used) * ROW_GAP;
    const topY = mid + Math.min(...used) * ROW_GAP, bottomY = mid + Math.max(...used) * ROW_GAP;
    const place = (point: Point, labelAbove: boolean): MetroStopPlace => ({ ...point, unitIndex, labelAbove, labelWidth: geometry.labelWidth });

    grid.columns.forEach((column, index) => column.forEach((lesson) => {
        const labelAbove = geometry.crowded && column.length === 1 && index % 2 === 1;
        layout.stops[lesson.id] = place({ x: geometry.columnX(index), y: mid + (lanes.get(lesson.id) ?? 0) * ROW_GAP }, labelAbove);
    }));
    spurs.forEach((spur, id) => {
        const y = spur.below ? bottomY + STOP_RADIUS + 92 + spur.row * SPUR_GAP : topY - STOP_RADIUS - 34 - geometry.lift - spur.row * SPUR_GAP;
        layout.stops[id] = place({ x: geometry.columnX(spur.column), y }, !spur.below);
    });

    const hubIn = { x: leftToRight ? HUB : METRO_WIDTH - HUB, y: mid };
    const hubOut = { x: leftToRight ? METRO_WIDTH - HUB : HUB, y: mid };
    if (previous) layout.segments.push(turnBetweenRows(previous, hubIn, unitIndex));
    connectRow({ main, need: grid.need, hubIn, hubOut, unitIndex, previous, geometry, leftToRight }, layout);
    connectSpurs(input, spurs, hubIn, layout);
    layout.rows.push({ unitIndex, top, leftToRight });

    const downBand = downRows ? (downRows - 1) * SPUR_GAP + 132 : 0;
    return { hubOut, main: main.map((lesson) => lesson.id), bottom: bottomY + STOP_RADIUS + 64 + downBand };
}

type RowGeometry = {
    columnX: (column: number) => number;
    gap: number;
    crowded: boolean;
    lift: number;
    labelWidth: number;
};

/** Columns spread evenly between the hubs; a row of many columns takes a little more of the width. */
function rowGeometry(count: number, leftToRight: boolean, isLast: boolean): RowGeometry {
    const first = count > 4 ? WIDE_FIRST_COLUMN : FIRST_COLUMN;
    const span = METRO_WIDTH - 2 * first - (isLast ? 80 : 0);
    const gap = count > 1 ? Math.min(MAX_COLUMN_GAP, span / (count - 1)) : 0;
    const start = first + (span - (count - 1) * gap) / 2;
    const crowded = gap < CROWDED_GAP;

    return {
        columnX: (column) => (leftToRight ? start + column * gap : METRO_WIDTH - start - column * gap),
        gap,
        crowded,
        lift: crowded ? LABEL_LIFT : 0,
        labelWidth: gap ? Math.round(Math.max(MIN_LABEL_WIDTH, Math.min(LABEL_WIDTH, gap - 10))) : LABEL_WIDTH,
    };
}

/** Columns from the row's start: one per lesson in a prerequisite's place, with in-group prerequisites first. */
function depthOf({ lessons, byId }: RowInput): (lesson: MetroLesson) => number {
    const memo = new Map<string, number>();
    const unitId = lessons[0]?.unitId;

    const depth = (lesson: MetroLesson): number => {
        const known = memo.get(lesson.id);
        if (known !== undefined) return known;

        const previous = lesson.previous
            .map((id) => byId.get(id))
            .filter((p): p is MetroLesson => !!p && p.unitId === unitId && (lesson.bonus || !p.bonus));
        const value = previous.length ? 1 + Math.max(...previous.map(depth)) : 0;

        memo.set(lesson.id, value);
        return value;
    };

    return depth;
}

type Grid = {
    columns: MetroLesson[][];
    /** The lessons a line leads from into each lesson of the row. */
    need: Map<string, string[]>;
    firstColumn: Record<number, number>;
    columnOf: Map<string, number>;
};

/** Stacks of up to three lessons, or four when three would need more columns than the row has room for. */
function buildGrid(input: RowInput, depth: (lesson: MetroLesson) => number): Grid {
    const grid = chunkColumns(input, depth, MAX_STACK);

    return grid.columns.length > MAX_COLUMNS ? chunkColumns(input, depth, WIDE_STACK) : grid;
}

/** A depth wider than `stack` is split into stacks that run in series, each track leading into the next stack. */
function chunkColumns(input: RowInput, depth: (lesson: MetroLesson) => number, stack: number): Grid {
    const main = input.lessons.filter((lesson) => !lesson.bonus);
    const need = new Map(main.map((lesson) => [ lesson.id, directPrerequisites(lesson, input) ]));
    const columns: MetroLesson[][] = [], firstColumn: Record<number, number> = {};
    const lastOfSeries = new Map<string, string>(), chained = new Set<string>();
    const maxDepth = Math.max(0, ...input.lessons.map(depth));

    for (let level = 0; level <= maxDepth; level++) {
        const chunks = chunk(main.filter((lesson) => depth(lesson) === level), stack);
        firstColumn[level] = columns.length;

        chunks.forEach((part, index) => {
            part.forEach((lesson, position) => {
                if (chunks.length > 1) lastOfSeries.set(lesson.id, chunks[chunks.length - 1][0].id);
                if (!index) return;

                const before = chunks[index - 1];
                chained.add(lesson.id);
                need.set(lesson.id, [ before[position % before.length].id ]);
            });
            columns.push(part);
        });
    }

    // A lesson after a series leaves from its end, so the line runs through the whole series first
    main.filter((lesson) => !chained.has(lesson.id)).forEach((lesson) => {
        need.set(lesson.id, unique((need.get(lesson.id) ?? []).map((id) => lastOfSeries.get(id) ?? id)));
    });

    const columnOf = new Map(columns.flatMap((column, index) => column.map((lesson) => [ lesson.id, index ] as const)));
    return { columns, need, firstColumn, columnOf };
}

/** In-group required prerequisites without the ones another prerequisite already implies. */
function directPrerequisites(lesson: MetroLesson, { lessons, byId }: RowInput): string[] {
    const inRow = (id: string) => {
        const other = byId.get(id);
        return !!other && other.unitId === lesson.unitId && !other.bonus && lessons.includes(other);
    };
    const ancestors = (id: string, seen = new Set<string>()): Set<string> => {
        (byId.get(id)?.previous ?? []).filter(inRow).forEach((p) => {
            if (!seen.has(p)) {
                seen.add(p);
                ancestors(p, seen);
            }
        });
        return seen;
    };

    const own = lesson.previous.filter(inRow);
    return own.filter((id) => !own.some((other) => other !== id && ancestors(other).has(id)));
}

/** The lessons a line leads from into `id`: its prerequisites, or the row's entry when it has none. */
function parentsOf(need: Map<string, string[]>, id: string): string[] {
    const own = need.get(id) ?? [];

    return own.length ? own : [ IN ];
}

function childrenOf(need: Map<string, string[]>): Map<string, string[]> {
    const children = new Map<string, string[]>();
    need.forEach((parents, id) => parents.forEach((parent) => children.set(parent, [ ...(children.get(parent) ?? []), id ])));

    return children;
}

/** The longest run of lessons that starts at a lesson; longer runs keep the lanes nearer the middle. */
function runLength(children: Map<string, string[]>): (id: string) => number {
    const memo = new Map<string, number>();
    const length = (id: string): number => {
        const known = memo.get(id);
        if (known !== undefined) return known;

        const value = 1 + Math.max(0, ...(children.get(id) ?? []).map(length));
        memo.set(id, value);
        return value;
    };

    return length;
}

/**
 * Lanes, as rows of tracks above (negative) and below (positive) the middle: a lesson takes its
 * prerequisites' lane, or the nearest one its line can reach without running through another.
 */
function assignLanes(input: RowInput, grid: Grid): Map<string, number> {
    const { columns, need, columnOf } = grid;
    const children = childrenOf(need);
    const length = runLength(children);
    const lanes = new Map<string, number>(), busy = new Map<number, Set<number>>();
    const laneOf = (id: string) => (id === IN ? 0 : lanes.get(id) ?? 0);
    const columnOfAny = (id: string) => (id === IN ? -1 : columnOf.get(id) ?? 0);
    const mark = (column: number, lane: number) => busy.set(column, new Set([ ...(busy.get(column) ?? []), lane ]));
    const range = (from: number, to: number) => Array.from({ length: Math.max(0, to - from) }, (_, index) => from + index);

    columns.forEach((column, index) => {
        [ ...column ].sort((a, b) => length(b.id) - length(a.id)).forEach((lesson) => {
            const parents = parentsOf(need, lesson.id);
            const wanted = parents.map(laneOf).sort((a, b) => a - b)[Math.floor((parents.length - 1) / 2)];
            const isLeaf = !children.get(lesson.id)?.length;
            // The cells this lesson's line holds: its own, the run from a single parent, and to the row's end after a leaf
            const cells = [ index, ...(parents.length === 1 ? range(columnOfAny(parents[0]) + 1, index) : []), ...(isLeaf ? range(index + 1, columns.length) : []) ];
            const step = LANE_STEPS.find((candidate) => cells.every((cell) => !busy.get(cell)?.has(wanted + candidate))) ?? 0;

            lanes.set(lesson.id, wanted + step);
            cells.forEach((cell) => mark(cell, wanted + step));
            if (parents.length > 1) parents.forEach((parent) => range(columnOfAny(parent) + 1, index).forEach((cell) => mark(cell, laneOf(parent))));
        });
    });

    untangle({ input, grid, lanes, children, length });
    return lanes;
}

type UntangleInput = {
    input: RowInput;
    grid: Grid;
    lanes: Map<string, number>;
    children: Map<string, string[]>;
    length: (id: string) => number;
};

/**
 * Swaps the lanes of a run of columns while that removes a clash, so a branch that starts later
 * sits inside the ones already running and no line crosses or covers another.
 */
function untangle(context: UntangleInput) {
    const cost = laneCost(context);

    let best = cost();
    for (let step = 0; step < UNTANGLE_STEPS && best >= CLASH; step++) {
        const move = bestSwap(context, cost);
        if (!move || move.cost >= best) break;

        swapLanes(context.lanes, move);
        best = move.cost;
    }
}

type LaneSwap = { cost: number; moved: string[]; a: number; b: number };

/** The swap of two lanes over a run of columns that leaves the row least tangled. */
function bestSwap({ grid, lanes }: UntangleInput, cost: () => number): LaneSwap | undefined {
    const ids = [ ...lanes.keys() ];
    const used = unique(ids.map((id) => lanes.get(id) ?? 0));
    const candidates = [ ...used, Math.min(...used) - 1, Math.max(...used) + 1 ];
    const columns = grid.columns.map((_, index) => index);
    let best: LaneSwap | undefined;

    columns.forEach((start) => columns.slice(start).forEach((end) => candidates.forEach((a) => candidates.filter((b) => a < b).forEach((b) => {
        const moved = ids.filter((id) => {
            const column = grid.columnOf.get(id) ?? 0, lane = lanes.get(id);
            return column >= start && column <= end && (lane === a || lane === b);
        });
        if (!moved.length) return;

        const option = { cost: 0, moved, a, b };
        swapLanes(lanes, option);
        option.cost = cost();
        swapLanes(lanes, option);
        if (!best || option.cost < best.cost) best = option;
    }))));

    return best;
}

function swapLanes(lanes: Map<string, number>, { moved, a, b }: LaneSwap) {
    moved.forEach((id) => lanes.set(id, lanes.get(id) === a ? b : a));
}

type Run = { edge: number; lane: number; start: number; end: number; from: number; to: number; parent: string };
type Jog = { edge: number; column: number; low: number; high: number };

/** How tangled the row's lines are: every clash costs CLASH, and every lane away from the middle a little. */
function laneCost({ input, grid, lanes, children, length }: UntangleInput): () => number {
    const ids = [ ...lanes.keys() ];
    const columnOfAny = (id: string) => (id === IN ? -1 : id === OUT ? grid.columns.length : grid.columnOf.get(id) ?? 0);
    const laneOf = (id: string) => (id === IN || id === OUT ? 0 : lanes.get(id) ?? 0);
    const leaves = ids.filter((id) => !children.get(id)?.length);
    const edges = [
        ...ids.flatMap((id) => {
            const parents = parentsOf(grid.need, id);
            return parents.map((parent) => ({ from: parent, to: id, join: parents.length > 1 }));
        }),
        ...(leaves.length > 1 ? leaves.map((id) => ({ from: id, to: OUT, join: true })) : []),
    ];
    const spurParents = ids.filter((id) => input.lessons.some((lesson) => lesson.bonus && lesson.previous.includes(id)));

    return () => {
        const runs: Run[] = [], jogs: Jog[] = [];
        let total = 0;

        edges.forEach((edge, index) => {
            const from = columnOfAny(edge.from), to = columnOfAny(edge.to), a = laneOf(edge.from), b = laneOf(edge.to);
            const straight = a === b;
            runs.push({
                edge: index, lane: edge.join ? a : b, from, to, parent: edge.from,
                start: straight || edge.join ? from : from + 1,
                end: straight ? to - 1 : edge.join ? to - 2 : to - 1,
            });
            if (straight) return;

            jogs.push({ edge: index, column: edge.join ? to - 1 : from, low: Math.min(a, b), high: Math.max(a, b) });
            total += Math.abs(a - b);
        });

        // A line stepping across another lane in a column where that lane is running crosses it
        jogs.forEach((jog) => runs.forEach((run) => {
            if (run.edge !== jog.edge && run.lane > jog.low && run.lane < jog.high && run.start <= jog.column && jog.column <= run.end) total += CLASH;
        }));
        // Two lines from different stops sharing a lane in the same columns cover each other
        runs.forEach((run, index) => runs.slice(index + 1).forEach((other) => {
            if (run.lane === other.lane && run.parent !== other.parent && Math.max(run.start, other.start) <= Math.min(run.end, other.end)) total += CLASH;
        }));
        ids.forEach((id) => { total += Math.abs(laneOf(id)) * length(id); });
        // A lesson with a bonus spur needs a free side in its column, as the spur drops straight from it
        spurParents.forEach((id) => {
            const column = ids.filter((other) => other !== id && grid.columnOf.get(other) === grid.columnOf.get(id));
            if (column.some((other) => laneOf(other) < laneOf(id)) && column.some((other) => laneOf(other) > laneOf(id))) total += CLASH;
        });
        // A line running along a lane through a column that has a stop on that lane goes through the stop
        runs.forEach((run) => ids.forEach((id) => {
            const column = columnOfAny(id);
            if (laneOf(id) === run.lane && column > run.from && column < run.to) total += CLASH;
        }));

        return total;
    };
}

type SpurPlace = {
    below: boolean;
    column: number;
    row: number;
};

/**
 * A bonus lesson hangs one column after its in-group prerequisite, on the side of the line that
 * prerequisite is on, unless another stop in its column blocks the straight drop there. A bonus
 * after another bonus stays on its row; a spur from the line takes the next free row.
 */
function arrangeSpurs({ lessons, byId }: RowInput, grid: Grid, lanes: Map<string, number>, depth: (lesson: MetroLesson) => number): Map<string, SpurPlace> {
    const places = new Map<string, SpurPlace>(), taken = new Set<string>();
    const used = [ 0, ...lanes.values() ];
    const middle = (Math.min(...used) + Math.max(...used)) / 2;
    const isBonusId = (id: string) => !!byId.get(id)?.bonus;
    const blocked = (id: string, side: number) => [ ...lanes.keys() ].some((other) =>
        other !== id && grid.columnOf.get(other) === grid.columnOf.get(id) && Math.sign((lanes.get(other) ?? 0) - (lanes.get(id) ?? 0)) === side);

    lessons.filter((lesson) => lesson.bonus).sort((a, b) => depth(a) - depth(b)).forEach((lesson) => {
        const parent = lesson.previous.find((id) => byId.get(id)?.unitId === lesson.unitId);
        const parentSpur = parent ? places.get(parent) : undefined;
        let below = parentSpur ? parentSpur.below : parent ? (lanes.get(parent) ?? 0) > middle : false;
        if (parent && !isBonusId(parent) && blocked(parent, below ? 1 : -1) && !blocked(parent, below ? -1 : 1)) below = !below;

        const parentColumn = parentSpur ? parentSpur.column : parent && !isBonusId(parent) ? grid.columnOf.get(parent) ?? 0 : grid.firstColumn[depth(lesson)];
        const column = parent ? Math.min(grid.columns.length - 1, parentColumn + 1) : grid.firstColumn[depth(lesson)];
        let row = parentSpur && parentSpur.below === below ? parentSpur.row : 0;
        while (taken.has(`${ below }:${ column }:${ row }`)) row++;

        taken.add(`${ below }:${ column }:${ row }`);
        places.set(lesson.id, { below, column, row });
    });

    return places;
}

function rowCount(spurs: Map<string, SpurPlace>, below: boolean): number {
    return Math.max(0, ...[ ...spurs.values() ].filter((spur) => spur.below === below).map((spur) => spur.row + 1));
}

type ConnectInput = {
    main: MetroLesson[];
    need: Map<string, string[]>;
    hubIn: Point;
    hubOut: Point;
    unitIndex: number;
    previous?: PlacedRow;
    geometry: RowGeometry;
    leftToRight: boolean;
};

/** Forks bend right after the stop they leave; every line into a join meets at one point just before it. */
function connectRow({ main, need, hubIn, hubOut, unitIndex, previous, geometry, leftToRight }: ConnectInput, layout: MetroLayout) {
    const entered = previous?.main ?? [];
    const all = main.map((lesson) => lesson.id);
    const at = (id: string) => (id === IN ? hubIn : id === OUT ? hubOut : layout.stops[id]);
    const travelledBy = (id: string) => (id === IN ? entered : id === OUT ? all : [ id ]);
    const add = (d: string, ids: string[]) => layout.segments.push({ d, unitIndex, lessons: unique(ids.flatMap(travelledBy)) });
    const direction = leftToRight ? 1 : -1;
    const bend = Math.max(2 * TURN_RADIUS + 6, Math.min(geometry.gap / 2 - 16, geometry.labelWidth / 2 + 4));

    const parents = new Map(all.map((id) => [ id, parentsOf(need, id) ]));
    const leaves = all.filter((id) => !all.some((other) => (need.get(other) ?? []).includes(id)));
    if (main.length) parents.set(OUT, leaves);

    const forks = new Map<string, string[]>();
    parents.forEach((from, id) => { if (from.length === 1) forks.set(from[0], [ ...(forks.get(from[0]) ?? []), id ]); });
    forks.forEach((children, parent) => {
        const a = at(parent);
        const room = Math.min(...children.map((id) => Math.abs(at(id).x - a.x)));
        const x = a.x + direction * Math.max(6, Math.min(bend, room - 2 * TURN_RADIUS - 8));

        children.forEach((id) => {
            const b = at(id);
            add(b.y === a.y ? line(a, b) : `M${ a.x } ${ a.y } ${ turn(x, a.y, b.y, TURN_RADIUS, direction) }L${ b.x } ${ b.y } `, [ parent, id ]);
        });
    });

    parents.forEach((from, id) => {
        if (from.length < 2) return;

        const b = at(id);
        const room = Math.min(...from.map((parent) => Math.abs(at(parent).x - b.x)));
        const merge = id === OUT ? 0 : Math.max(0, Math.min(bend, room - 2 * TURN_RADIUS - 8));
        const mx = b.x - direction * merge;
        from.forEach((parent) => {
            const a = at(parent), radius = id === OUT ? EXIT_TURN_RADIUS : TURN_RADIUS;
            add(a.y === b.y ? line(a, { x: mx, y: b.y }) : `M${ a.x } ${ a.y } ${ turn(mx - direction * 2 * radius, a.y, b.y, radius, direction) }L${ mx } ${ b.y } `, [ parent, id ]);
        });
        if (merge) add(line({ x: mx, y: b.y }, b), [ id, ...from ]);
    });

    if (!main.length) add(segment(hubIn, hubOut), [ IN ]);
}

/** A spur drops straight from its prerequisite, or from the nearest stop when the prerequisite is elsewhere, then turns into the bonus stop. */
function connectSpurs({ lessons }: RowInput, spurs: Map<string, SpurPlace>, hubIn: Point, layout: MetroLayout) {
    const main = lessons.filter((lesson) => !lesson.bonus);

    spurs.forEach((spur, id) => {
        const b = layout.stops[id];
        const parent = lessons.find((lesson) => lesson.id === id)?.previous.find((other) => lessons.some((lesson) => lesson.id === other));
        const sameColumn = main.filter((lesson) => Math.abs(layout.stops[lesson.id].x - b.x) < 1)
            .sort((m, n) => (layout.stops[m.id].y - layout.stops[n.id].y) * (spur.below ? -1 : 1));
        const nearest = sameColumn[0] ?? [ ...main ].sort((m, n) => Math.abs(layout.stops[m.id].x - b.x) - Math.abs(layout.stops[n.id].x - b.x))[0];
        const a = parent ? layout.stops[parent] : nearest ? layout.stops[nearest.id] : hubIn;

        layout.spurs.push(Math.abs(a.y - b.y) < 1 || Math.abs(a.x - b.x) < 1 ? line(a, b) : drop(a, b));
    });
}

function drop(a: Point, b: Point): string {
    const sy = Math.sign(b.y - a.y), sx = Math.sign(b.x - a.x), r = Math.min(18, Math.abs(b.x - a.x) / 2);

    return `M${ a.x } ${ a.y } L${ a.x } ${ b.y - sy * r } Q${ a.x } ${ b.y } ${ a.x + sx * r } ${ b.y } L${ b.x } ${ b.y } `;
}

/** From the current point along `y1` to `x`, then a rounded step over to `y2`. */
function turn(x: number, y1: number, y2: number, radius: number, direction: number): string {
    const sy = Math.sign(y2 - y1), r = Math.min(radius, Math.abs(y2 - y1) / 2), x1 = x + direction * r;

    return `L${ x } ${ y1 } Q${ x1 } ${ y1 } ${ x1 } ${ y1 + sy * r } L${ x1 } ${ y2 - sy * r } Q${ x1 } ${ y2 } ${ x + direction * 2 * r } ${ y2 } `;
}

function line(a: Point, b: Point): string {
    return `M${ a.x } ${ a.y } L${ b.x } ${ b.y } `;
}

function turnBetweenRows(previous: PlacedRow, hubIn: Point, unitIndex: number): MetroSegment {
    const { x, y } = previous.hubOut;
    const side = hubIn.x < METRO_WIDTH / 2 ? SIDE : METRO_WIDTH - SIDE;
    const d = `M${ x } ${ y } Q${ side } ${ y } ${ side } ${ y + 60 } L${ side } ${ hubIn.y - 60 } Q${ side } ${ hubIn.y } ${ hubIn.x } ${ hubIn.y } `;

    return { d, unitIndex, lessons: previous.main };
}

/** Metro style: straight, a 45° diagonal, straight; a step too steep for that bends with rounded corners. */
function segment(a: Point, b: Point): string {
    const dx = b.x - a.x, dy = b.y - a.y, sx = Math.sign(dx) || 1, ady = Math.abs(dy), adx = Math.abs(dx);
    const mx = (a.x + b.x) / 2;

    if (!dy) return line(a, b);
    if (ady <= adx - 24) return `M${ a.x } ${ a.y } L${ mx - sx * ady / 2 } ${ a.y } L${ mx + sx * ady / 2 } ${ b.y } L${ b.x } ${ b.y } `;

    const sy = Math.sign(dy), r = Math.min(22, ady / 2, adx / 4);
    return `M${ a.x } ${ a.y } L${ mx - sx * r } ${ a.y } Q${ mx } ${ a.y } ${ mx } ${ a.y + sy * r } `
        + `L${ mx } ${ b.y - sy * r } Q${ mx } ${ b.y } ${ mx + sx * r } ${ b.y } L${ b.x } ${ b.y } `;
}

function obstacles(layout: MetroLayout): Rect[] {
    const stops = Object.values(layout.stops).flatMap((stop) => [
        { x: stop.x - 26, y: stop.y - 26, w: 52, h: 52 },
        { x: stop.x - stop.labelWidth / 2, y: stop.labelAbove ? stop.y - 70 : stop.y + 29, w: stop.labelWidth, h: 40 },
    ]);
    const plaques = layout.rows.map((row) => ({ x: row.leftToRight ? METRO_WIDTH - 72 - 380 : 72, y: row.top + 10, w: 380, h: 40 }));

    return [ ...stops, ...plaques ];
}

/** Points along every line, close enough together to tell whether a box would sit on one. */
function sampleLines(paths: string[]): Point[] {
    return paths.flatMap((path) => path.split('M').filter(Boolean).flatMap((piece) => {
        const numbers = (piece.match(/-?[\d.]+/g) ?? []).map(Number);
        const points: Point[] = [];
        for (let i = 0; i + 1 < numbers.length; i += 2) points.push({ x: numbers[i], y: numbers[i + 1] });

        return points.slice(1).flatMap((to, i) => {
            const from = points[i];
            return Array.from({ length: 21 }, (_, step) => ({ x: from.x + (to.x - from.x) * step / 20, y: from.y + (to.y - from.y) * step / 20 }));
        });
    }));
}

function fits(rect: Rect, layout: MetroLayout): boolean {
    return rect.x >= 8 && rect.x + rect.w <= layout.width - 70 && rect.y >= 8;
}

function overlaps(a: Rect, b: Rect): boolean {
    return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

function near(rect: Rect, point: Point): boolean {
    return point.x > rect.x - 10 && point.x < rect.x + rect.w + 10 && point.y > rect.y - 10 && point.y < rect.y + rect.h + 10;
}

/** `items` as one group when they fit in `size`, else as consecutive groups of `size`. */
function chunk<T>(items: T[], size: number): T[][] {
    if (items.length <= size) return [ items ];

    return Array.from({ length: Math.ceil(items.length / size) }, (_, index) => items.slice(index * size, (index + 1) * size));
}

function unique<T>(items: T[]): T[] {
    return [ ...new Set(items) ];
}
